/**
 * Défilement accéléré de l'écran titre des coulisses et rattrapage de la figure 02 (mode mobile,
 * `description.md:29` ; plan-figures §5.B point 5 ; plan-ecrans §4 bis point 6). Origine commune :
 * `passed = hdrH − rect(.pin).top`, nulle quand l'écran titre est seul.
 *
 * Titre : en flux (1× par le compositeur) et `--rush-y` (1× de plus, `RUSH_K`), lue par la règle C1 de
 * `styles/mobile.ts`. Suiveur (1er `.figslot`, porteur de l'écran FIG 02) : `--rush-f` / `--rush-c`, lues par C10
 * de `styles/mobile.ts` qui compose la translation en unités d'écran (terme A : bas du petit viewport quand le
 * titre est seul, puis `RUSH_FOLLOW_K` ; terme C : translation nulle quand son haut de mise en page atteint
 * l'en-tête ; jamais au-dessus de son palier) ; repli sans JS : translation nulle. Longueurs de mise en page
 * relatives au `.pin` par `offsetWithin` (insensibles aux translations).
 *
 * Passe 9 (écran titre) : le palier du titre est rendu par la feuille (C1 `sticky` centré, réserve C1k, retrait C1s,
 * `styles/mobile.ts`) ; ici, l'origine de l'accélération est décalée de `shift` (`rushOrigin`), `rushLift` / `rushFollow`
 * étant appelés avec `passed − shift` et `reach − shift`. `--rush-h` (`RUSH_TITLE_VAR`) est écrite sur le porteur
 * `.chslot`, en px entiers avec zone morte `WRITE_DEADBAND_PX` ; une lecture de `getComputedStyle(#ch-head).top` par
 * image. Piège R-9b : l'`offsetTop` d'un élément `sticky` inclut son décalage de collage, les décalages du titre ne
 * sont donc plus jamais lus (le porteur les remplace).
 *
 * Garanties : un seul écouteur `scroll` passif par instance, regroupé par rAF, pour les deux éléments (R-8m) ;
 * un rectangle lu par image (R-7l) ; écritures conditionnelles, muettes hors de [0, reach] et une fois le titre
 * sorti ; écriture tout ou rien par image ; aucune écriture de défilement, aucune minuterie, aucun abonnement au
 * changement de mode : la sonde est rejouée par le moteur (`engine/scene-engine.ts:364,530`).
 *
 * Consommateurs : `scenes/coulisses.tsx` (`createRush`), `styles/mobile.ts` (`RUSH_VAR`, `RUSH_FOLLOW_VARS`, `RUSH_TITLE_VAR`, `RUSH_HOLD_SVH`).
 */
import { clamp } from "./math";
import { isMobile, isReduced } from "./env";
import { offsetWithin } from "./offset";
import { CADRAGE_HOLD_SVH } from "./cadrage";
import { WRITE_DEADBAND_PX } from "./cadrage-bind"; // cadrage-bind n'importe scene-engine/scene-manifest qu'en type : aucun cycle
import type { EngineDeps } from "./scene-engine"; // type seul : aucun cycle à l'exécution (comme cadrage-bind.ts:21)

/** Rapport de vitesse au doigt une fois le titre seul à l'écran (« environ deux fois », `description.md:29`) ; réglage unique. */
export const RUSH_K = 2;

/** Variable CSS écrite sur `#ch-head`, lue par la règle C1 de `styles/mobile.ts`. */
export const RUSH_VAR = "--rush-y";

/**
 * Rapport de vitesse au doigt du suiveur (figure 02) une fois le titre seul (« environ trois fois », `description.md:29`).
 * Arbitrage A2 (plan-ecrans §10, R-8c) : à 3, la figure 02 n'est pas au contact du titre à l'instant de sa sortie
 * (haut à ≈ 384 px à 402 × 714) ; le contact à la lettre demanderait ≈ 6-7. Réglage unique, à juger sur appareil ;
 * aucun test n'en épingle la valeur (seulement `RUSH_FOLLOW_K > RUSH_K`).
 */
export const RUSH_FOLLOW_K = 3;

/**
 * Variables CSS écrites sur le suiveur (1er `#cl-grid > .figslot`), lues par la seule règle C10 de `styles/mobile.ts` :
 * `lead` = hdrH + reach + (K − 1)·pc (repli CSS `100svh`), `cap` = (K − 1)·(reach − pc) (repli CSS `0px`).
 * Noms disjoints de `--cad-*`, écrits sur le même porteur par `engine/cadrage-bind.ts`.
 */
export const RUSH_FOLLOW_VARS = { lead: "--rush-f", cap: "--rush-c" } as const;

/**
 * Partie « seul et centré » du palier du titre, en `svh` (« palier court, comme les autres écrans », `description.md:29`) ;
 * lue par `styles/mobile.ts` (C1k) seulement. Réglage propre au titre (remplacer l'alias par un nombre, ex. 20, si
 * jugé long sur appareil, R-9c).
 */
export const RUSH_HOLD_SVH: number = CADRAGE_HOLD_SVH;

/**
 * Hauteur de `#ch-head` en px entiers, écrite sur le porteur `.chslot` par `createRush`, lue par C1, C1s, C1k de
 * `styles/mobile.ts` ; repli CSS `CAD_NO_MEASURE` → pas de palier.
 */
export const RUSH_TITLE_VAR = "--rush-h";

/**
 * Translation verticale ajoutée au titre, en px (≤ 0), non croissante en `passed`, pure.
 * @param passed défilement depuis l'instant « seul à l'écran » (≤ 0 avant)
 * @param span hauteur du bloc titre (au-delà, constante : le titre est hors champ)
 * @param k rapport de vitesse au doigt (≥ 1)
 * @returns `−(k − 1) × clamp(passed, 0, max(0, span))` ; `0` (jamais `−0`) si nulle ou non finie
 */
export function rushLift(passed: number, span: number, k: number = RUSH_K): number {
	const lift = -(k - 1) * clamp(passed, 0, Math.max(0, span));
	return Number.isFinite(lift) && lift !== 0 ? lift : 0;
}

/**
 * Longueurs du suiveur, en px de mise en page, pures.
 * @param passed défilement depuis l'instant « titre seul » (≤ 0 avant)
 * @param reach haut de mise en page du suiveur relatif au `.pin` (au-delà, constantes : suiveur sur son palier)
 * @param k rapport de vitesse au doigt (> 1)
 * @returns `lead = reach + (k − 1)·pc` et `cap = (k − 1)·(reach − pc)`, `pc = clamp(passed, 0, max(0, reach))` ;
 *   `null` si `passed`, `reach` ou `k` n'est pas fini ; jamais `−0`
 */
export function rushFollow(passed: number, reach: number, k: number = RUSH_FOLLOW_K): { readonly lead: number; readonly cap: number } | null {
	if (!Number.isFinite(passed) || !Number.isFinite(reach) || !Number.isFinite(k)) return null;
	const pc = clamp(passed, 0, Math.max(0, reach));
	const lead = reach + (k - 1) * pc;
	const cap = (k - 1) * (reach - pc);
	return { lead: lead === 0 ? 0 : lead, cap: cap === 0 ? 0 : cap };
}

/** Mesures de mise en page du titre, en px relatifs au `.pin` (`stickyTop` = `top` calculé du titre collant). */
export type RushTitleLayout = {
	readonly hdrH: number;
	readonly slotTop: number;
	readonly slotH: number;
	readonly titleH: number;
	readonly stickyTop: number;
};

/**
 * Origine décalée de l'accélération du titre, pure.
 * Palier (réserve `slotH − titleH ≥ 1` et `stickyTop` fini) : `shift = max(0, hdrH + slotTop + slotH − titleH − stickyTop)`
 * (= `G + R − Δc`, soit `Hp` quand `A > 0`) et `span = max(0, stickyTop + titleH − hdrH)`.
 * Sinon : `shift = 0`, `span = max(0, slotTop + titleH)` (passe 8).
 *
 * Repli R-9a, « dégradé, pas identique » : si `stickyTop` est illisible (`NaN`) alors qu'une réserve existe,
 * `shift = 0` et `span` de la passe 8, mais la réserve CSS (C1k) et le collage (C1) restent posés : le titre collé
 * reçoit `--rush-y` dès `passed = 0` (il n'est plus immobile pendant le palier) et la figure 02 part trop tôt, sa
 * portée incluant la réserve. Ce n'est pas la passe 8 à l'identique ; seule la branche « réserve < 1 px » (ou sans
 * mesure) l'est.
 * @param l mesures de mise en page (px relatifs au `.pin`)
 * @returns `shift` et `span` (≥ 0, jamais `−0`) ; `null` si `hdrH`, `slotTop`, `slotH` ou `titleH` n'est pas fini
 */
export function rushOrigin(l: RushTitleLayout): { readonly shift: number; readonly span: number } | null {
	const { hdrH, slotTop, slotH, titleH, stickyTop } = l;
	if (!Number.isFinite(hdrH) || !Number.isFinite(slotTop) || !Number.isFinite(slotH) || !Number.isFinite(titleH)) return null;
	if (slotH - titleH >= 1 && Number.isFinite(stickyTop)) {
		return {
			shift: Math.max(0, hdrH + slotTop + slotH - titleH - stickyTop),
			span: Math.max(0, stickyTop + titleH - hdrH),
		};
	}
	return { shift: 0, span: Math.max(0, slotTop + titleH) };
}

/** Poignée de la liaison : `probe` (rejouée par le moteur) et `dispose` (idempotent). */
export type RushHandle = { readonly probe: () => void; readonly dispose: () => void };

/**
 * Nœuds liés : `origin` = `.pin` des coulisses (origine du défilement), `slot` = `.chslot`, porteur non collant de
 * `#ch-head` (bloc conteneur de son `sticky`, réserve `::after`), requis, `title` = `#ch-head`,
 * `follower` = 1er `#cl-grid > .figslot` (porteur de l'écran FIG 02).
 */
export type RushElements = {
	readonly origin: HTMLElement;
	readonly slot: HTMLElement;
	readonly title: HTMLElement;
	readonly follower: HTMLElement;
};

/**
 * Lie `--rush-y` du titre et `--rush-f` / `--rush-c` du suiveur à la position de défilement (mobile hors mouvement
 * réduit). Création sans effet de bord ; hors mobile ou en mouvement réduit, la sonde retire les variables et l'écouteur.
 * @param els porteur (origine), titre et suiveur
 * @param deps rAF/cAF injectables (tests)
 * @returns `probe` et `dispose`
 */
export function createRush(els: RushElements, deps?: EngineDeps): RushHandle {
	const { origin, slot, title, follower } = els;
	const raf = deps?.raf ?? ((cb: FrameRequestCallback) => requestAnimationFrame(cb));
	const caf = deps?.caf ?? ((h: number) => cancelAnimationFrame(h));
	let hdrH = 0;
	let listening = false;
	let pending: number | null = null;
	// caches locaux, un par variable : écriture conditionnelle sans relire le style
	let wy: string | null = null;
	let wf: string | null = null;
	let wc: string | null = null;
	// cache numérique de --rush-h (px entiers), sans relire le style
	let wh: number | null = null;

	const tick = (): void => {
		if (!isMobile() || isReduced()) return;
		const op = origin.offsetParent;
		if (!(op instanceof HTMLElement)) return; // jsdom, scène non affichée : rien
		// décalages lus AVANT le rectangle ; tout ou rien : les valeurs précédentes restent si une chaîne est rompue.
		// Le titre (`sticky`) n'est jamais mesuré par offsetWithin (R-9b) : le porteur `slot` le remplace.
		const base = offsetWithin(origin, op);
		const s = offsetWithin(slot, op);
		let f = offsetWithin(follower, op);
		if (base === null || s === null || f === null) return;
		const titleH = title.offsetHeight;
		if (!Number.isFinite(titleH)) return;
		const h = Math.round(titleH);
		if (wh === null || Math.abs(h - wh) >= WRITE_DEADBAND_PX) {
			wh = h;
			slot.style.setProperty(RUSH_TITLE_VAR, `${h}px`);
			// la réserve et le retrait CSS ont changé : relire le suiveur
			f = offsetWithin(follower, op);
			if (f === null) return;
		}
		// lectures APRÈS l'écriture éventuelle de --rush-h
		const slotH = slot.offsetHeight;
		const stickyTop = parseFloat(getComputedStyle(title).top);
		const passed = hdrH - origin.getBoundingClientRect().top;
		const o = rushOrigin({ hdrH, slotTop: s - base, slotH, titleH, stickyTop });
		if (o === null) return;
		const fol = rushFollow(passed - o.shift, f - base - o.shift);
		if (fol === null) return;
		const y = `${rushLift(passed - o.shift, o.span).toFixed(1)}px`;
		const lead = `${(hdrH + fol.lead).toFixed(1)}px`;
		const cap = `${fol.cap.toFixed(1)}px`;
		if (y !== wy) {
			wy = y;
			title.style.setProperty(RUSH_VAR, y);
		}
		if (lead !== wf) {
			wf = lead;
			follower.style.setProperty(RUSH_FOLLOW_VARS.lead, lead);
		}
		if (cap !== wc) {
			wc = cap;
			follower.style.setProperty(RUSH_FOLLOW_VARS.cap, cap);
		}
	};

	const onScroll = (): void => {
		if (pending !== null) return;
		pending = raf(() => {
			pending = null;
			tick();
		});
	};

	const stop = (): void => {
		if (listening) {
			window.removeEventListener("scroll", onScroll);
			listening = false;
		}
		if (pending !== null) {
			caf(pending);
			pending = null;
		}
	};

	const clear = (): void => {
		wy = wf = wc = null;
		wh = null;
		slot.style.removeProperty(RUSH_TITLE_VAR);
		title.style.removeProperty(RUSH_VAR);
		follower.style.removeProperty(RUSH_FOLLOW_VARS.lead);
		follower.style.removeProperty(RUSH_FOLLOW_VARS.cap);
	};

	const probe = (): void => {
		if (!isMobile() || isReduced()) {
			stop();
			clear();
			return;
		}
		hdrH = document.querySelector<HTMLElement>(".hdr")?.offsetHeight ?? 0; // comme cockpit.opening.ts:128
		if (!listening) {
			window.addEventListener("scroll", onScroll, { passive: true });
			listening = true;
		}
		tick();
	};

	const dispose = (): void => {
		stop();
		clear();
	};

	return { probe, dispose };
}
